import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules/schema';
import { fromMilli, milliCost, toMilli } from './fixed';
import { buildingLevel } from './levels';
import { cityStores, isMilitary, storesType } from './occupancy';
import { canAfford, holdsStock } from './stock';
import { territoryOwners } from './territory';
import type { Building, EntityId, PlayerId, ResourceId, SimState, Unit } from './types';

/**
 * Veterancy: units earn xp for damage dealt (`veterancy.xpPerDamage`, or
 * `xpPerBuildingDamage` for damage to a building), and
 * each rank reached (`veterancy.ranks` xp thresholds) adds `hpPerRank` of the
 * type's hp and `strengthPerRank` of its strength. A rank can also be bought
 * at a barracks (`train`), which sets xp to that rank's threshold.
 */

/** A unit's rank (0 = none): how many `veterancy.ranks` thresholds its xp reaches. */
function unitRank(rules: RuleSet, unit: Unit): number {
  const xp = unit.xp ?? 0;
  let rank = 0;
  for (const need of rules.veterancy.ranks) {
    if (xp < need) break;
    rank++;
  }
  return rank;
}

/** Highest rank there is. */
function maxRank(rules: RuleSet): number {
  return rules.veterancy.ranks.length;
}

/** Max hp, in thousandths (fixed.ts): the type's hp plus `hpPerRank` of it per rank. */
function unitMaxHp(rules: RuleSet, unit: Unit): number {
  const def = rules.units[unit.type];
  if (!def) return unit.hp;
  return toMilli(def.hp * (1 + rules.veterancy.hpPerRank * unitRank(rules, unit)));
}

/** Damage per second: the type's strength plus `strengthPerRank` of it per rank. */
function unitStrength(rules: RuleSet, unit: Unit): number {
  const strength = rules.units[unit.type]?.strength ?? 0;
  return strength * (1 + rules.veterancy.strengthPerRank * unitRank(rules, unit));
}

/**
 * Sets a unit's xp; on a rank change its hp is scaled so it keeps the same
 * fraction of its max.
 */
function setXp(rules: RuleSet, unit: Unit, xp: number): void {
  const before = unitMaxHp(rules, unit);
  unit.xp = xp;
  const after = unitMaxHp(rules, unit);
  if (after !== before && before > 0) unit.hp = Math.round((unit.hp * after) / before);
}

/**
 * Adds xp for `damage` dealt (in thousandths of hp); damage to a building earns
 * `xpPerBuildingDamage` (default `xpPerDamage`), so shelling walls ranks
 * siege units up slower than fighting.
 */
function gainXp(rules: RuleSet, unit: Unit, damage: number, onBuilding = false): void {
  const { xpPerDamage, xpPerBuildingDamage } = rules.veterancy;
  const xp = fromMilli(damage) * (onBuilding ? (xpPerBuildingDamage ?? xpPerDamage) : xpPerDamage);
  if (xp > 0) setXp(rules, unit, (unit.xp ?? 0) + xp);
}

/** What training to `rank` costs: `veterancy.trainCost` × rank. */
function trainCost(rules: RuleSet, rank: number): Record<ResourceId, number> {
  const cost: Record<ResourceId, number> = {};
  for (const [res, amount] of Object.entries(rules.veterancy.trainCost)) cost[res] = amount * rank;
  return cost;
}

/**
 * The own completed barracks (highest level, then oldest) of the own city
 * whose territory `unit` stands in; undefined when there's none. "Barracks"
 * is whichever store takes the unit's type: a vehicle trains at a factory.
 */
function trainingBarracks(state: SimState, rules: RuleSet, unit: Unit): Building | undefined {
  const owners = territoryOwners(state, rules);
  const city = state.buildings[owners.get(getHexTileMapKey(unit.pos)) ?? ''];
  if (!city || city.owner !== unit.owner || !holdsStock(rules, city)) return undefined;
  let best: Building | undefined;
  for (const b of cityStores(state, rules, city.id, owners)) {
    if (!storesType(rules, b, unit.type)) continue;
    if (!best || buildingLevel(b) > buildingLevel(best)) best = b;
  }
  return best;
}

type TrainRejection = 'unknown' | 'no-barracks' | 'max-rank' | 'barracks-level' | 'cost';

type TrainCheck = { ok: true; rank: number } | { ok: false; reason: TrainRejection };

/**
 * Whether `playerId` may train `unitId` a rank now. Shared by the `train`
 * command and the Train button. Checked in order:
 *   - unknown: no such unit, not theirs, not military, or the player is out
 *   - no-barracks: it isn't in the territory of an own city with a
 *     completed barracks (stored units are: they stand on the barracks)
 *   - max-rank: it already has the top rank
 *   - barracks-level: the next rank is above that barracks' level
 *   - cost: their cities together don't hold `trainCost(next rank)`
 */
function canTrain(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  unitId: EntityId
): TrainCheck {
  const unit = state.units[unitId];
  const player = state.players[playerId];
  if (!unit || unit.owner !== playerId || !isMilitary(rules, unit.type) || !player) {
    return { ok: false, reason: 'unknown' };
  }
  if (player.eliminated) return { ok: false, reason: 'unknown' };
  const barracks = trainingBarracks(state, rules, unit);
  if (!barracks) return { ok: false, reason: 'no-barracks' };
  const rank = unitRank(rules, unit) + 1;
  if (rank > maxRank(rules)) return { ok: false, reason: 'max-rank' };
  if (rank > buildingLevel(barracks)) return { ok: false, reason: 'barracks-level' };
  if (!canAfford(state, playerId, milliCost(trainCost(rules, rank)))) {
    return { ok: false, reason: 'cost' };
  }
  return { ok: true, rank };
}

export type { TrainCheck, TrainRejection };
export { canTrain, gainXp, setXp, trainCost, unitMaxHp, unitRank, unitStrength };
